// Concept2 PM5 Bluetooth Service UUIDs and Characteristics
// Based on Concept2 PM Bluetooth Smart Communication Interface Definition
// Updated with correct parsing from official specs

// C2 PM Base UUID Format: CE06XXXX-43E5-11E4-916C-0800200C9A66
const C2_BASE_UUID = 'ce06{0}-43e5-11e4-916c-0800200c9a66';

// Helper to generate full UUID from 16-bit short ID
function c2Uuid(shortId) {
  return C2_BASE_UUID.replace('{0}', shortId.toString(16).padStart(4, '0'));
}

// PM5 Service UUIDs
export const PM5_SERVICES = {
  DEVICE_INFO: c2Uuid(0x0003),      // Device Information Service
  PM_CONTROL: c2Uuid(0x0020),       // C2 PM Control Service  
  PM_ROWING: c2Uuid(0x0030),        // C2 PM Rowing Service (main data service)
};

// The main rowing service UUID (most commonly used)
export const PM5_SERVICE_UUID = PM5_SERVICES.PM_ROWING;

// PM5 Characteristics (within Rowing Service 0x0030)
export const PM5_CHARACTERISTICS = {
  // Rowing General Status (0x0031) - elapsed time, distance, workout state
  GENERAL_STATUS: c2Uuid(0x0031),
  
  // Rowing Additional Status 1 (0x0032) - speed, stroke rate, heart rate, pace, power
  ADDITIONAL_STATUS_1: c2Uuid(0x0032),
  
  // Rowing Additional Status 2 (0x0033) - interval data
  ADDITIONAL_STATUS_2: c2Uuid(0x0033),
  
  // Rowing Stroke Data (0x0034) - stroke count, drive info
  STROKE_DATA: c2Uuid(0x0034),
  
  // Rowing Additional Stroke Data (0x0035) - drive time, stroke recovery
  ADDITIONAL_STROKE_DATA: c2Uuid(0x0035),
  
  // Rowing Split/Interval Data (0x0036)
  SPLIT_INTERVAL_DATA: c2Uuid(0x0036),
  
  // End of Workout Summary (0x0039)
  END_OF_WORKOUT_SUMMARY: c2Uuid(0x0039),
  
  // Power Curve (0x003D)
  POWER_CURVE: c2Uuid(0x003D),
  
  // Legacy names for backward compatibility
  ROWING_STATUS: c2Uuid(0x0034),
  EXTRA_ROWING_DATA_1: c2Uuid(0x0035),
  EXTRA_ROWING_DATA_2: c2Uuid(0x0036),
  HEART_RATE: c2Uuid(0x0039),
  
  // Control characteristics (in PM Control Service 0x0020)
  MULTIPLEXED_INFO: c2Uuid(0x0080),
  CONTROL: c2Uuid(0x0081),
  TRANSMIT: c2Uuid(0x0082),
  RECEIVE: c2Uuid(0x0083),
};

// Workout States
export const WORKOUT_STATE = {
  WAITING_TO_BEGIN: 0,
  WORKOUT_ROW: 1,
  COUNTDOWN_PAUSE: 2,
  INTERVAL_REST: 3,
  WORK_TIME_INTERVAL: 4,
  WORK_DISTANCE_INTERVAL: 5,
  REST_END_WORKOUT_IN_PROGRESS: 6,
  TERMINATE_WORKOUT: 7,
  WORKOUT_END: 8,
  WORKOUT_LOGGED: 9,
  WORKOUT_REARM: 10
};

// Rowing State
export const ROWING_STATE = {
  INACTIVE: 0,
  ACTIVE: 1
};

// Stroke State  
export const STROKE_STATE = {
  WAITING_FOR_MIN_SPEED: 0,
  WAITING_FOR_ACCELERATION: 1,
  DRIVING: 2,
  DWELLING_AFTER_DRIVE: 3,
  RECOVERY: 4
};

/**
 * Rowing Metrics data structure
 */
export class RowingMetrics {
  constructor() {
    this.elapsedTime = 0;      // seconds
    this.distance = 0;         // meters
    this.speed = 0;            // m/s
    this.strokeRate = 0;       // strokes per minute
    this.heartRate = 0;        // bpm
    this.pace = 0;             // seconds per 500m (split)
    this.strokeCount = 0;      // total strokes
    this.driveLength = 0;      // meters
    this.driveTime = 0;        // seconds
    this.power = 0;            // watts
    this.calories = 0;         // kcal
    this.dragFactor = 0;       // drag factor
    this.workoutState = 0;
    this.rowingState = 0;
    this.strokeState = 0;
  }
}

/**
 * PM5 Bluetooth Connection Class
 * Handles connection, notifications, and data parsing
 */
export class PM5Connection {
  constructor() {
    this.device = null;
    this.server = null;
    this.rowingService = null;
    this.characteristics = {};
    this.isConnected = false;
    this.listeners = {};
    this.metrics = new RowingMetrics();
    this.lastUpdate = 0;
    this.lastCalorieTime = 0;  // For calorie accumulation tracking
  }
  
  /**
   * Reset metrics for a new workout session
   */
  resetMetrics() {
    this.metrics = new RowingMetrics();
    this.metrics.accumulatedCalories = 0;  // Reset accumulated calories
    this.lastUpdate = 0;
    this.lastCalorieTime = 0;
    console.log('PM5: Metrics reset for new session');
  }

  /**
   * Connect to PM5 via Web Bluetooth
   */
  async connect() {
    try {
      if (!navigator.bluetooth) {
        throw new Error('Web Bluetooth API not supported in this browser');
      }

      console.log('PM5: Requesting device...');
      
      // Request PM5 device - filter by name prefix
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: 'PM5' }
        ],
        // Every PM5 service (Concept2 BLE interface definition): discovery 0x0000,
        // device info 0x0010, control 0x0020, rowing 0x0030 + standard GATT services.
        optionalServices: [
          c2Uuid(0x0000),
          c2Uuid(0x0010),
          PM5_SERVICES.PM_ROWING,
          PM5_SERVICES.PM_CONTROL,
          PM5_SERVICES.DEVICE_INFO,
          'device_information',
          'battery_service',
        ]
      });

      console.log('PM5: Device selected:', this.device.name);

      // Connect to GATT server with retry logic
      let retries = 3;
      while (retries > 0) {
        try {
          console.log('PM5: Connecting to GATT server...');
          this.server = await this.device.gatt.connect();
          break;
        } catch (gattError) {
          retries--;
          console.warn(`PM5: GATT connect failed, retries left: ${retries}`, gattError);
          if (retries === 0) throw gattError;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      console.log('PM5: GATT server connected');

      // Get the Rowing Service
      try {
        console.log('PM5: Getting rowing service...');
        this.rowingService = await this.server.getPrimaryService(PM5_SERVICES.PM_ROWING);
        console.log('PM5: Rowing service found');
      } catch (serviceError) {
        console.warn('PM5: Could not get rowing service directly, trying discovery...');
        
        // Try to discover all services
        const services = await this.server.getPrimaryServices();
        console.log('PM5: Available services:', services.map(s => s.uuid));
        
        // Look for the rowing service
        for (const service of services) {
          const uuid = service.uuid.toLowerCase();
          if (uuid.includes('0030') || uuid === PM5_SERVICES.PM_ROWING.toLowerCase()) {
            this.rowingService = service;
            console.log('PM5: Found rowing service via discovery:', uuid);
            break;
          }
        }
        
        if (!this.rowingService) {
          throw new Error('PM5 Rowing Service not found. Ensure PM5 is awake and in range.');
        }
      }
      
      // Get all characteristics from the rowing service
      try {
        console.log('PM5: Getting characteristics...');
        const chars = await this.rowingService.getCharacteristics();
        console.log('PM5: Found', chars.length, 'characteristics');
        
        for (const char of chars) {
          const uuid = char.uuid.toLowerCase();
          this.characteristics[uuid] = char;
          console.log('PM5: Characteristic:', uuid);
        }
      } catch (charError) {
        console.warn('PM5: Error getting characteristics:', charError);
      }

      this.isConnected = true;
      
      // Setup disconnect handler
      this.device.addEventListener('gattserverdisconnected', () => {
        this.handleDisconnect();
      });

      console.log('PM5: Connection complete');
      return true;
      
    } catch (error) {
      console.error('PM5 connection failed:', error);
      
      if (error.message.includes('User cancelled')) {
        throw new Error('Connection cancelled');
      } else if (error.message.includes('not supported')) {
        throw new Error('Bluetooth not supported. Use Chrome or Edge browser.');
      }
      
      throw error;
    }
  }

  /**
   * Start notifications for rowing data
   */
  async startNotifications() {
    console.log('PM5: Starting notifications...');
    
    // Priority order for characteristics to subscribe to
    const charPriority = [
      { uuid: PM5_CHARACTERISTICS.ADDITIONAL_STATUS_1, parser: this.parseAdditionalStatus1.bind(this), name: 'Additional Status 1' },
      { uuid: PM5_CHARACTERISTICS.GENERAL_STATUS, parser: this.parseGeneralStatus.bind(this), name: 'General Status' },
      { uuid: PM5_CHARACTERISTICS.STROKE_DATA, parser: this.parseStrokeData.bind(this), name: 'Stroke Data' },
      { uuid: PM5_CHARACTERISTICS.ADDITIONAL_STROKE_DATA, parser: this.parseAdditionalStrokeData.bind(this), name: 'Additional Stroke Data' },
    ];
    
    let subscribed = 0;
    
    for (const { uuid, parser, name } of charPriority) {
      const char = this.characteristics[uuid.toLowerCase()] || this.characteristics[uuid];
      if (char) {
        try {
          await char.startNotifications();
          char.addEventListener('characteristicvaluechanged', (event) => {
            try {
              parser(event.target.value);
            } catch (parseError) {
              console.warn(`PM5: Error parsing ${name}:`, parseError);
            }
          });
          console.log(`PM5: Subscribed to ${name}`);
          subscribed++;
        } catch (e) {
          console.warn(`PM5: Could not subscribe to ${name}:`, e.message);
        }
      } else {
        console.log(`PM5: ${name} characteristic not found`);
      }
    }
    
    if (subscribed === 0) {
      throw new Error('Could not subscribe to any PM5 characteristics');
    }
    
    console.log(`PM5: Subscribed to ${subscribed} characteristics`);
    return true;
  }

  /**
   * Parse Additional Status 1 (0x0032)
   * Per Concept2 BLE spec (17 bytes):
   * - Bytes 0-2: Elapsed Time (0.01 sec)
   * - Bytes 3-4: Speed (0.001 m/s) 
   * - Byte 5: Stroke Rate (spm)
   * - Byte 6: Heart Rate (bpm, 255=invalid)
   * - Bytes 7-8: Current Pace (0.01 sec/500m)
   * - Bytes 9-10: Average Power (watts) - 2 bytes
   * - Bytes 11-12: Calories/hour (kcal/hr)
   * - Bytes 13-14: Calories Total (kcal) - cumulative
   * - Bytes 15-16: Additional data
   */
  parseAdditionalStatus1(dataView) {
    // Log raw bytes for debugging
    const bytes = [];
    for (let i = 0; i < dataView.byteLength; i++) {
      bytes.push(dataView.getUint8(i));
    }
    console.log('PM5 [0x0032] Raw bytes:', bytes.map(b => b.toString(16).padStart(2, '0')).join(' '), `(${dataView.byteLength} bytes)`);
    
    if (dataView.byteLength < 9) {
      console.warn('PM5: Additional Status 1 too short:', dataView.byteLength);
      return;
    }
    
    // Bytes 0-2: Elapsed time (0.01 sec resolution) - 3 bytes little-endian
    const elapsedTimeRaw = dataView.getUint8(0) | (dataView.getUint8(1) << 8) | (dataView.getUint8(2) << 16);
    const elapsedTime = elapsedTimeRaw / 100;
    
    // Bytes 3-4: Speed (0.001 m/s) - 2 bytes little-endian
    const speedRaw = dataView.getUint16(3, true);
    const speed = speedRaw / 1000;
    
    // Byte 5: Stroke rate (strokes per minute)
    const strokeRate = dataView.getUint8(5);
    
    // Byte 6: Heart rate (bpm, 255 = invalid/no HR monitor)
    const heartRateRaw = dataView.getUint8(6);
    const heartRate = (heartRateRaw > 0 && heartRateRaw < 255) ? heartRateRaw : 0;
    
    // Bytes 7-8: Current pace (0.01 sec/500m) - 2 bytes little-endian
    const paceRaw = dataView.getUint16(7, true);
    const pace = Math.round(paceRaw / 100); // Convert to whole seconds per 500m
    
    // Bytes 9-10: Average Power (watts) - 2 bytes
    // NOTE: This value can be unreliable in some PM5 firmware versions
    // We'll calculate power from speed instead using Concept2 formula
    let powerRaw = 0;
    if (dataView.byteLength >= 11) {
      powerRaw = dataView.getUint16(9, true);
    }
    
    // Calculate power from speed using Concept2 formula: P = 2.8 × v³
    // This is more reliable than the raw power bytes which can be incorrect
    const power = speed > 0 ? Math.round(2.8 * Math.pow(speed, 3)) : 0;
    
    // Bytes 11-12: Calories/hour rate (kcal/hr) - often zeros in BLE
    let calPerHourRaw = 0;
    if (dataView.byteLength >= 13) {
      calPerHourRaw = dataView.getUint16(11, true);
    }
    
    // Bytes 13-14: Cumulative Calories (kcal) - often zeros in BLE
    let caloriesRaw = 0;
    if (dataView.byteLength >= 15) {
      caloriesRaw = dataView.getUint16(13, true);
    }
    
    // Calculate cal/hr using Concept2-style formula (without HR monitor)
    // PM5 uses a proprietary formula. This approximation provides reasonable accuracy.
    // Base metabolic contribution is higher during exercise than resting.
    const calPerHour = calPerHourRaw > 0 ? calPerHourRaw : (speed > 0 ? Math.round(9.6 * Math.pow(speed, 3) + 350) : 0);
    
    // ACCUMULATE calories over time instead of calculating instantaneously
    // This better matches how the PM5 tracks calories
    const timeDelta = this.lastCalorieTime > 0 ? (elapsedTime - this.lastCalorieTime) : 0;
    this.lastCalorieTime = elapsedTime;
    
    let calories = caloriesRaw;
    if (caloriesRaw === 0) {
      // Add incremental calories for this time period
      if (timeDelta > 0 && timeDelta < 5 && speed > 0) {
        // calories this period = cal/hr × (time in hours)
        const incrementalCal = calPerHour * (timeDelta / 3600);
        this.metrics.accumulatedCalories = (this.metrics.accumulatedCalories || 0) + incrementalCal;
      }
      calories = Math.round(this.metrics.accumulatedCalories || 0);
    }
    
    // Calculate stroke count from stroke rate and elapsed time
    const strokeCount = (strokeRate > 0 && elapsedTime > 0) 
      ? Math.round((strokeRate / 60) * elapsedTime)
      : 0;
    
    // Smooth the speed using EMA (reduces flickering)
    if (speed > 0) {
      if (this.metrics.speed > 0) {
        this.metrics.speed = this.metrics.speed * 0.6 + speed * 0.4;
      } else {
        this.metrics.speed = speed;
      }
    }
    
    // Use pace directly from PM5 (PM5 already smooths it)
    if (pace > 0 && pace < 600) {
      this.metrics.pace = pace;
    }
    
    // Debug: Log parsed values  
    console.log(`PM5 Parsed: time=${elapsedTime.toFixed(1)}s, speed=${speed.toFixed(3)}m/s, rate=${strokeRate}spm, HR=${heartRate}bpm, pace=${pace}s (raw:${paceRaw}), power=${power}W (raw:${powerRaw}), calHr=${calPerHour}, cal=${calories}, strokes=${strokeCount}`);
    
    // Update metrics
    this.metrics.elapsedTime = elapsedTime;
    this.metrics.strokeRate = strokeRate;
    this.metrics.heartRate = heartRate;
    this.metrics.power = power;
    this.metrics.strokeCount = strokeCount;
    this.metrics.calories = calories;
    
    // NOTE: rowing-status (elapsed time + distance) is emitted by parseGeneralStatus
    // only — 0x0031 is the authoritative source for both values. Emitting it
    // here from 0x0032 caused stale distance values (this.metrics.distance is
    // only updated by parseGeneralStatus) to overwrite fresh ones at ~4 Hz.

    this.emit('extra-rowing-data', {
      strokeRate: this.metrics.strokeRate,
      pace: this.metrics.pace,
      power: this.metrics.power,
      calories: this.metrics.calories,
      speed: this.metrics.speed,
      strokeCount: this.metrics.strokeCount,
    });
    
    // Emit heart rate if valid
    if (heartRate > 0) {
      window.dispatchEvent(new CustomEvent('heart-rate-update', { 
        detail: heartRate 
      }));
    }
    
    // Emit debug data for on-screen display
    window.dispatchEvent(new CustomEvent('pm5-debug', {
      detail: {
        rawBytes: bytes.map(b => b.toString(16).padStart(2, '0')).join(' '),
        byteCount: dataView.byteLength,
        elapsedTime: elapsedTime.toFixed(1),
        distance: Math.round(this.metrics.distance),
        strokeRate: strokeRate,
        heartRate: heartRate,
        pace: pace,
        paceRaw: paceRaw,
        speed: speed.toFixed(3),
        power: power,
        powerRaw: powerRaw,
        calPerHour: calPerHour,
        calories: calories,
        strokeCount: this.metrics.strokeCount
      }
    }));
  }

  /**
   * Parse General Status (0x0031)
   * Contains: elapsed time, distance, workout state, rowing state
   * This is the most reliable source for distance
   */
  parseGeneralStatus(dataView) {
    // Log raw bytes for debugging
    const bytes = [];
    for (let i = 0; i < dataView.byteLength; i++) {
      bytes.push(dataView.getUint8(i));
    }
    console.log('PM5 [0x0031] Raw bytes:', bytes.map(b => b.toString(16).padStart(2, '0')).join(' '), `(${dataView.byteLength} bytes)`);
    
    if (dataView.byteLength < 6) {
      console.warn('PM5: General Status too short:', dataView.byteLength);
      return;
    }
    
    // Bytes 0-2: Elapsed time (0.01 sec)
    const elapsedTimeRaw = dataView.getUint8(0) | (dataView.getUint8(1) << 8) | (dataView.getUint8(2) << 16);
    const elapsedTime = elapsedTimeRaw / 100;
    
    // Bytes 3-5: Distance (0.1 meters) - This is the actual PM5 distance
    const distanceRaw = dataView.getUint8(3) | (dataView.getUint8(4) << 8) | (dataView.getUint8(5) << 16);
    const distance = distanceRaw / 10;
    
    console.log(`PM5 [0x0031] Parsed: time=${elapsedTime.toFixed(1)}s (raw:${elapsedTimeRaw}), dist=${distance.toFixed(1)}m (raw:${distanceRaw})`);
    
    // Parse additional fields if present
    let workoutType = 0, intervalType = 0, workoutState = 0, rowingState = 0, strokeState = 0;
    let totalWorkDistance = 0, workoutDuration = 0, dragFactor = 0;
    
    if (dataView.byteLength >= 11) {
      workoutType = dataView.getUint8(6);
      intervalType = dataView.getUint8(7);
      workoutState = dataView.getUint8(8);
      rowingState = dataView.getUint8(9);
      strokeState = dataView.getUint8(10);
    }
    
    if (dataView.byteLength >= 17) {
      totalWorkDistance = (dataView.getUint8(11) | (dataView.getUint8(12) << 8) | (dataView.getUint8(13) << 16)) / 10;
      workoutDuration = (dataView.getUint8(14) | (dataView.getUint8(15) << 8) | (dataView.getUint8(16) << 16)) / 100;
    }
    
    if (dataView.byteLength >= 19) {
      dragFactor = dataView.getUint8(18);
    }
    
    // Update metrics - use actual distance from PM5
    this.metrics.elapsedTime = elapsedTime;
    this.metrics.distance = distance;
    this.metrics.workoutState = workoutState;
    this.metrics.rowingState = rowingState;
    this.metrics.strokeState = strokeState;
    this.metrics.dragFactor = dragFactor;
    
    // Emit rowing-status event with actual PM5 distance
    this.emit('rowing-status', {
      elapsedTime: this.metrics.elapsedTime,
      distance: Math.round(this.metrics.distance),
    });
    
    // Emit general status
    this.emit('general-status', {
      elapsedTime,
      distance,
      workoutType,
      intervalType,
      workoutState,
      rowingState,
      strokeState,
      totalWorkDistance,
      workoutDuration,
      dragFactor
    });
  }

  /**
   * Parse Stroke Data (0x0034)
   * Contains: drive length, drive time (stroke count from this characteristic is unreliable)
   */
  parseStrokeData(dataView) {
    // Log raw bytes for debugging
    const bytes = [];
    for (let i = 0; i < dataView.byteLength; i++) {
      bytes.push(dataView.getUint8(i));
    }
    console.log('PM5 [0x0034] Raw bytes:', bytes.map(b => b.toString(16).padStart(2, '0')).join(' '), `(${dataView.byteLength} bytes)`);
    
    if (dataView.byteLength < 8) return;
    
    // Bytes 0-2: Elapsed time (0.01 sec)
    const elapsedTime = (dataView.getUint8(0) | 
                        (dataView.getUint8(1) << 8) | 
                        (dataView.getUint8(2) << 16)) / 100;
    
    // Bytes 3-5: Distance (0.1 meters)
    const distance = (dataView.getUint8(3) | 
                     (dataView.getUint8(4) << 8) | 
                     (dataView.getUint8(5) << 16)) / 10;
    
    // Bytes 6-7: Drive length (0.01 meters)
    const driveLength = dataView.getUint16(6, true) / 100;
    
    // Byte 8: Drive time (0.01 sec)
    const driveTime = dataView.byteLength > 8 ? dataView.getUint8(8) / 100 : 0;
    
    // Bytes 9-10: Stroke recovery time (0.01 sec)
    const recoveryTime = dataView.byteLength > 10 ? dataView.getUint16(9, true) / 100 : 0;
    
    // NOTE: Stroke count from 0x0034 is unreliable - we calculate it from rate × time in 0x0032
    // Bytes 11-12 contain something but not accurate stroke count
    
    console.log(`PM5 [0x0034] Parsed: driveLength=${driveLength}m, driveTime=${driveTime}s`);
    
    this.metrics.driveLength = driveLength;
    this.metrics.driveTime = driveTime;
    this.metrics.distance = distance;
    // DO NOT set strokeCount here - it comes from 0x0032 calculation
    
    // Emit stroke data (without stroke count)
    this.emit('stroke-data', {
      elapsedTime,
      distance,
      driveLength,
      driveTime,
      recoveryTime
    });
  }

  /**
   * Parse Additional Stroke Data (0x0035)
   * Contains: stroke power, work per stroke
   */
  parseAdditionalStrokeData(dataView) {
    if (dataView.byteLength < 15) return;
    
    // Bytes 0-2: Elapsed time (0.01 sec)
    const elapsedTime = (dataView.getUint8(0) | 
                        (dataView.getUint8(1) << 8) | 
                        (dataView.getUint8(2) << 16)) / 100;
    
    // Byte 3: Stroke power (watts)
    // Actually bytes 3-4 in some specs
    const strokePower = dataView.getUint16(3, true);
    
    // Bytes 5-6: Stroke calories (0.1 kcal)
    const strokeCalories = dataView.getUint16(5, true) / 10;
    
    // Bytes 7-8: Stroke count
    const strokeCount = dataView.getUint16(7, true);
    
    // Bytes 9-12: Work per stroke (0.1 Joules) - appears to be 4 bytes
    // Or could be: Bytes 9-10: Projected work time, Bytes 11-12: Projected work distance
    
    // ── Single-source guarantee (2026-06 bug fix) ────────────────────────────
    // 0x0036 broadcasts *per-stroke* power + a per-hour calorie-burn figure,
    // whereas 0x0032 broadcasts the running *average power* and running *total
    // calories*. Writing both into the same fields caused the Monitor tiles
    // to flicker at ~2 Hz as the two characteristics fought for the same
    // slots. Per the rower's request, we keep PWR + CAL owned exclusively by
    // 0x0032. This handler still emits the raw per-stroke values on the
    // 'additional-stroke-data' event so downstream code (e.g. rhythm ratio,
    // stroke replay) that specifically needs stroke-level power can subscribe.
    this.metrics.strokeCount = strokeCount;
    
    this.emit('additional-stroke-data', {
      elapsedTime,
      strokePower,
      strokeCalories,
      strokeCount
    });
  }

  /**
   * Event emitter methods
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error(`PM5: Error in ${event} listener:`, e);
      }
    });
    
    // Also emit as window events for global listeners
    window.dispatchEvent(new CustomEvent(`pm5-${event}`, { detail: data }));
  }

  handleDisconnect() {
    console.log('PM5: Disconnected');
    this.isConnected = false;
    this.emit('disconnected', {});
  }

  async disconnect() {
    if (this.device && this.device.gatt.connected) {
      await this.device.gatt.disconnect();
    }
    this.isConnected = false;
    this.device = null;
    this.server = null;
    this.rowingService = null;
    this.characteristics = {};
  }
  
  /**
   * Get current metrics snapshot
   */
  getMetrics() {
    return { ...this.metrics };
  }
}

// Export a singleton instance that can be accessed globally
let globalPM5Instance = null;

export function getPM5Connection() {
  if (!globalPM5Instance) {
    globalPM5Instance = new PM5Connection();
  }
  return globalPM5Instance;
}

export function resetPM5Connection() {
  if (globalPM5Instance) {
    globalPM5Instance.disconnect();
    globalPM5Instance = null;
  }
}

